import { getChildBundleItemsBySKU } from 'hooks/useStorefront'
import {
  classifyWarehouseStatus,
  combineWarehouseVerdicts,
  getMetafieldKey,
  getStockStatus,
  parseStockJson,
  pickEarliestDate,
  resolveActiveWarehouse
} from 'helpers/stock-status'

const STATUS_OUT_OF_STOCK = 'Out of stock'
const STATUS_PRE_ORDER = 'Pre-order'
const STATUS_AVAILABLE = 'available'

const BADGE_READY_TO_SHIP = 'Ready to Ship'
const BADGE_PRE_ORDER = 'Pre-order'

/**
 * Parses a `linked_products.package_items` value — "SKU-A#2, SKU-B#1" — into a bundle composition.
 * A missing qty is left as NaN so the caller skips that child from the buildable count rather than
 * assuming a quantity.
 *
 * @param { string|null|undefined } raw
 * @returns { Array<{ sku: string, qty: number }> }
 */
export function parseBundleComposition (raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map((childItem) => ({
      sku: childItem.split('#')[0].trim(),
      qty: parseInt(childItem.split('#')[1])
    }))
    .filter((childItem) => childItem.sku !== '')
}

// unknown (no data at all) is worse than outofstock (has data, just not sellable), which is
// worse than preorder, which is worse than instock.
const WAREHOUSE_STATUS_SEVERITY = { unknown: 3, outofstock: 2, preorder: 1, instock: 0 }

/**
 * Rolls the bundle up across every child. Worst child wins: any 'unknown' child makes the whole
 * bundle unknown, otherwise any out-of-stock child makes it out of stock, otherwise any preorder
 * child makes it preorder. You cannot build a bundle without every one of its children.
 */
const degradeWarehouseStatus = (current, next) =>
  WAREHOUSE_STATUS_SEVERITY[next] > WAREHOUSE_STATUS_SEVERITY[current] ? next : current

/**
 * Resolves one child against both warehouses at once, for the no-postcode path. Children of a
 * single bundle may be stocked in different warehouses, so each is judged on whichever warehouse
 * can best supply it — the same per-child hopping resolveActiveWarehouse does once a postcode is
 * set. Returns the child's verdict plus the best (earliest) preorder date backing it, if any.
 */
const resolveChildAcrossWarehouses = (variantLike) => {
  const brisbaneStock = parseStockJson(variantLike.metafields.brisbane_stock_json)
  const melbourneStock = parseStockJson(variantLike.metafields.melbourne_stock_json)
  const brisbaneVerdict = classifyWarehouseStatus(brisbaneStock)
  const melbourneVerdict = classifyWarehouseStatus(melbourneStock)
  const combined = combineWarehouseVerdicts(brisbaneVerdict, melbourneVerdict)

  // Only a warehouse actually offering the preorder has a date worth quoting, and if both do we
  // would source from whichever arrives first.
  const candidateDates = [
    brisbaneVerdict === 'preorder' ? brisbaneStock?.earliestpodate : null,
    melbourneVerdict === 'preorder' ? melbourneStock?.earliestpodate : null
  ]
  const poDate = combined === 'preorder' ? pickEarliestDate(candidateDates) : null

  return {
    // 'fallback' means neither warehouse had usable data for this child.
    verdict: combined === 'fallback' ? 'unknown' : combined,
    poDate,
    brisbaneVerdict,
    melbourneVerdict
  }
}

/**
 * Resolves the rolled-up stock status of a bundle from its child items.
 *
 * With a postcode set, each child is resolved against its *own* warehouse blob
 * (custom.brisbane-stock-json / custom.melbourne-stock-json) — a bundle parent carries no blob of
 * its own. Rules: any child out of stock or SKU not found -> Out of stock; else any child preorder
 * -> Pre-order; else available.
 *
 * With no postcode, each child is still resolved individually — against whichever of the two
 * warehouses can best supply it, since a bundle may legitimately be assembled from both — and the
 * worst child then decides the bundle. Only a child neither warehouse has data for falls back to
 * Shopify's native availability. This keeps the answer stable when a shopper later enters a
 * postcode, which is what resolveActiveWarehouse would have worked out per child anyway.
 *
 * `badgeStatus` is the same no-postcode rollup expressed as a stock badge, mirroring the
 * single-product rule in plp-card / product-gallery.
 *
 * @param { Array<{ sku: string, qty: number }> } bundleChildItems
 * @param { { warehouse1P?: string|null, warehouse2P?: string|null } } warehouses
 * @returns { Promise<{ childrenBundleStatus: 'Out of stock' | 'Pre-order' | 'available', poDates: Array<string>, bundleStockQty: number|null, badgeStatus: 'Ready to Ship' | 'Pre-order' | null }> }
 */
const getChildStockStatus = async (bundleChildItems, { warehouse1P = null, warehouse2P = null } = {}) => {
  let enableLogging = false

  if (!bundleChildItems?.length) {
    return { childrenBundleStatus: STATUS_OUT_OF_STOCK, poDates: [], bundleStockQty: null, badgeStatus: null }
  }

  if (['127.0.0.1', 'localhost'].includes(location.hostname)) {
    enableLogging = true
  }

  if (enableLogging) {
    console.log('bundleChildItems', bundleChildItems)
  }

  const childProductsSkus = bundleChildItems.map((childItem) => childItem.sku)
  const variantsBySku = await getChildBundleItemsBySKU(childProductsSkus)
    .then((res) => {
      if (!res.ok || !res.json) {
        throw res
      }
      return res.json()
    })
    .then((body) => {
      if (body.errors) {
        throw body
      }

      // Flatten every returned variant into a sku -> variant lookup. Search can return products
      // that only fuzzy-match the query, so keying by sku is what makes "SKU not found" reliable.
      const lookup = {}
      body.data.search.edges.forEach(({ node }) => {
        node?.variants?.edges?.forEach(({ node: variant }) => {
          if (variant?.sku) {
            lookup[variant.sku] = variant
          }
        })
      })

      if (enableLogging) {
        console.log('bundle child variants by sku', lookup)
      }

      return lookup
    })
    .catch((err) => {
      console.error('Some error during getChildStockStatus', err)
      return {}
    })

  const hasPostcode = warehouse1P !== null || warehouse2P !== null
  const poDates = []
  // Collected per child on the no-postcode path, only used if the rollup lands on 'preorder'.
  const noPostcodePoDates = []
  let hasOutOfStockChild = false
  let hasPreorderChild = false
  // Buildable count: how many whole bundles the scarcest child can supply. Only derivable from a
  // warehouse blob, so it stays null on the no-postcode path.
  let minBuildable = null
  // Worst-child rollup across the bundle, used when there is no postcode to pick a warehouse with.
  let noPostcodeRollup = 'instock'

  bundleChildItems.forEach((childItem) => {
    const variant = variantsBySku[childItem.sku]

    // SKU not found — treat the whole bundle as out of stock rather than silently ignoring it.
    if (!variant) {
      hasOutOfStockChild = true
      noPostcodeRollup = degradeWarehouseStatus(noPostcodeRollup, 'unknown')
      if (enableLogging) {
        console.log('bundle child', { sku: childItem.sku, verdict: 'sku not found -> out of stock' })
      }
      return
    }

    // getStockStatus reads `available`; the Storefront API calls it `availableForSale`.
    const variantLike = {
      available: variant.availableForSale,
      metafields: {
        brisbane_stock_json: variant.brisbaneStockJson?.value,
        melbourne_stock_json: variant.melbourneStockJson?.value
      }
    }

    if (!hasPostcode) {
      // Judge this child against whichever warehouse can best supply it, then let it degrade the
      // bundle. The rollup drives both the badge and the ATC-gating status — see the step below.
      const { verdict, poDate, brisbaneVerdict, melbourneVerdict } = resolveChildAcrossWarehouses(variantLike)
      noPostcodeRollup = degradeWarehouseStatus(noPostcodeRollup, verdict)
      if (poDate) {
        noPostcodePoDates.push(poDate)
      }

      // Shopify's own availability is only used as a fallback signal when no warehouse has data
      // for a child — see the step below.
      const isOutOfStock = variant.availableForSale === false
      if (isOutOfStock) {
        hasOutOfStockChild = true
      }
      if (enableLogging) {
        console.log('bundle child', {
          sku: childItem.sku,
          availableForSale: variant.availableForSale,
          brisbane: brisbaneVerdict,
          melbourne: melbourneVerdict,
          poDate,
          verdict
        })
      }
      return
    }

    const activeWarehouse = resolveActiveWarehouse(warehouse1P, warehouse2P, variantLike)
    const stockData = activeWarehouse
      ? parseStockJson(variantLike.metafields[getMetafieldKey(activeWarehouse)])
      : null
    const { isPreorder, isOutOfStock } = getStockStatus(stockData, variantLike, 0)

    if (isOutOfStock) {
      hasOutOfStockChild = true
    } else if (isPreorder) {
      hasPreorderChild = true
      if (stockData?.earliestpodate) {
        poDates.push(stockData.earliestpodate)
      }
    }

    // A child with no usable onhandstock has no opinion on the buildable count.
    const onHand = Number(stockData?.onhandstock)
    const qty = Number(childItem.qty)
    if (Number.isFinite(onHand) && Number.isFinite(qty) && qty > 0) {
      const buildable = Math.floor(onHand / qty)
      minBuildable = minBuildable === null ? buildable : Math.min(minBuildable, buildable)
    }

    if (enableLogging) {
      console.log('bundle child', {
        sku: childItem.sku,
        activeWarehouse,
        stockstatusmessage: stockData?.stockstatusmessage,
        onhandstock: stockData?.onhandstock,
        verdict: isOutOfStock ? 'out of stock' : isPreorder ? 'preorder' : 'in stock'
      })
    }
  })

  let childrenBundleStatus = STATUS_AVAILABLE

  if (hasPostcode) {
    if (hasOutOfStockChild) {
      childrenBundleStatus = STATUS_OUT_OF_STOCK
    } else if (hasPreorderChild) {
      childrenBundleStatus = STATUS_PRE_ORDER
    }
  } else {
    // No postcode — every child has already been resolved against its best warehouse, so the
    // worst of them decides the bundle. Only 'unknown' (a child no warehouse had data for) falls
    // back to Shopify's own availability.
    if (noPostcodeRollup === 'outofstock') {
      childrenBundleStatus = STATUS_OUT_OF_STOCK
    } else if (noPostcodeRollup === 'preorder') {
      childrenBundleStatus = STATUS_PRE_ORDER
      poDates.push(...noPostcodePoDates)
    } else if (noPostcodeRollup === 'instock') {
      childrenBundleStatus = STATUS_AVAILABLE
    } else {
      childrenBundleStatus = hasOutOfStockChild ? STATUS_OUT_OF_STOCK : STATUS_AVAILABLE
    }
  }

  // An available rollup must never read as "Only 0 left" — the rollup deliberately ignores qty, so
  // the buildable count can disagree with it.
  const bundleStockQty = childrenBundleStatus === STATUS_AVAILABLE && minBuildable !== null
    ? Math.max(1, minBuildable)
    : minBuildable

  let badgeStatus = null

  if (hasPostcode) {
    // An active warehouse already picked a side, so the badge is just the rollup restated.
    if (childrenBundleStatus === STATUS_AVAILABLE) badgeStatus = BADGE_READY_TO_SHIP
    else if (childrenBundleStatus === STATUS_PRE_ORDER) badgeStatus = BADGE_PRE_ORDER
  } else if (noPostcodeRollup === 'instock') {
    // No postcode — the badge is the same rollup the button already used, so the two can never
    // disagree. `hasOutOfStockChild` deliberately does not gate this: on this path it comes from
    // Shopify's native availability, which is the fallback signal, not the authority.
    badgeStatus = BADGE_READY_TO_SHIP
  } else if (noPostcodeRollup === 'preorder') {
    badgeStatus = BADGE_PRE_ORDER
  }

  if (enableLogging) {
    console.log('childrenBundleStatus', childrenBundleStatus, poDates, bundleStockQty, badgeStatus)
  }

  return { childrenBundleStatus, poDates, bundleStockQty, badgeStatus }
}

// A bundle's rollup only depends on its composition and the resolved warehouses, so the same query
// would otherwise be repeated for every card, cart line and PDP surface sharing a bundle, and on
// every re-render. Cache by that key.
const rollupCache = new Map()

/**
 * Cached, de-duplicated `getChildStockStatus`. Concurrent callers with the same composition and
 * warehouses share a single Storefront request.
 *
 * @param { Array<{ sku: string, qty: number }> } composition
 * @param { { warehouse1P?: string|null, warehouse2P?: string|null } } warehouses
 */
const getBundleStockRollup = (composition, { warehouse1P = null, warehouse2P = null } = {}) => {
  const cacheKey = `${composition.map((x) => x.sku + x.qty).join()}|${warehouse1P}|${warehouse2P}`

  if (rollupCache.has(cacheKey)) {
    return rollupCache.get(cacheKey)
  }

  const request = getChildStockStatus(composition, { warehouse1P, warehouse2P }).catch((err) => {
    // Don't cache a failure — a later mount should be able to retry.
    rollupCache.delete(cacheKey)
    throw err
  })

  rollupCache.set(cacheKey, request)
  return request
}

export { getChildStockStatus, getBundleStockRollup }
